/**
 * Screen manager: title, daily, career, quick, how-to, settings, pause,
 * half-time and report card. Owns the save store and match-start logic.
 */
import './screens.css';
import type { MatchConfig, ResultSummary } from '../../core/contracts';
import { h, prefersReducedMotion } from '../dom';
import { ensureFonts } from '../fonts';
import { applySettings } from '../applySettings';
import { createSaveStore } from '../../meta/storage';
import { DAILY_RULES, dailyNumber, dailySeed, dateKey } from '../../meta/daily';
import { nextCareerMatch, type Difficulty } from '../../meta/career';
import { bestGraphicsPackage, unlockedCameraKinds } from '../../meta/unlocks';
import type { MatchExtras, StartMatchOptions } from '../../meta/progress';
import type { HalfTimeParams, ScreenCtx, ScreenDeps, ScreenName, TitleParams, TrainingReportParams } from './context';
import type { ScreenInstance } from './common';
import { titleScreen } from './title';
import { quickScreen } from './quick';
import { dailyScreen } from './daily';
import { careerScreen } from './career';
import { howtoScreen } from './howto';
import { settingsScreen } from './settings';
import { halftimeScreen, pauseScreen } from './pause';
import { reportScreen } from './report';
import { trainingReportScreen, trainingSkipScreen } from './training';
import { TRAINING_COMPETITION, TRAINING_HALF_S, TRAINING_SEED } from '../../tutorial/config';

export type { ScreenDeps, ScreenName, HalfTimeParams, TrainingReportParams } from './context';
export type { StartMatchOptions, MatchExtras } from '../../meta/progress';

export interface Screens {
  readonly el: HTMLElement;
  readonly store: ScreenCtx['store'];
  /** Show a screen. 'none' hides everything (match is live). */
  show(name: ScreenName, params?: unknown): void;
  hide(): void;
  current(): ScreenName;
  /** True while any screen is up — gate director hotkeys on `!screens.isOpen()`. */
  isOpen(): boolean;
  showReport(summary: ResultSummary, extras?: MatchExtras): void;
  showHalfTime(p: HalfTimeParams): void;
  showPause(): void;
  startDaily(): void;
  startCareer(): void;
  startQuick(seed: number, difficulty: Difficulty, halfLengthSeconds: number): void;
  startTraining(): void;
  lastStart(): { config: MatchConfig; options: StartMatchOptions } | null;
  /** Play the broadcast stinger wipe; resolves when the screen is fully covered. */
  stinger(): Promise<void>;
  destroy(): void;
}

const QUICK_AUDIENCE: Record<Difficulty, number> = { rookie: 120_000, pro: 2_100_000, legend: 18_000_000 };

export function createScreens(root: HTMLElement, deps: ScreenDeps): Screens {
  ensureFonts();
  const store = deps.store ?? createSaveStore();
  applySettings(store.get().settings);

  const el = h('div', { class: 'sd-screens', attrs: { 'data-ui': 'screens' } });
  const stingerEl = h('div', { class: 'sd-stinger' }, [
    h('div', { class: 'sd-stinger__band sd-stinger__band--1' }),
    h('div', { class: 'sd-stinger__band sd-stinger__band--2' }),
    h('div', { class: 'sd-stinger__band sd-stinger__band--3' }, [h('span', { class: 'sd-stinger__logo' }, [h('span', { class: 'sd-brand-dot' }), 'SPORTS DIRECTOR'])]),
  ]);
  const notifyEl = h('div', { class: 'sd-notify' });
  root.append(el, stingerEl, notifyEl);

  let cur: { name: ScreenName; inst: ScreenInstance } | null = null;
  const history: ScreenName[] = [];
  let backOverride: ScreenName | null = null;
  let last: { config: MatchConfig; options: StartMatchOptions } | null = null;
  const now = () => (deps.now ? deps.now() : new Date());

  const options = (mode: StartMatchOptions['mode'], difficulty: Difficulty, half: number, baseAudience: number, extra: Partial<StartMatchOptions> = {}): StartMatchOptions => {
    const s = store.get();
    const fair = mode === 'daily';
    return {
      mode, difficulty, halfLengthSeconds: half, baseAudience,
      unlockedCameras: fair ? [] : unlockedCameraKinds(s.unlocks),
      graphicsPackage: fair ? 'gfx_basic' : bestGraphicsPackage(s.unlocks),
      punchMode: s.settings.punchMode,
      ...extra,
    };
  };

  async function launch(config: MatchConfig, opts: StartMatchOptions) {
    last = { config, options: opts };
    await stinger();
    show('none');
    deps.onStartMatch(config, opts);
  }

  const ctx: ScreenCtx = {
    deps, store, now,
    show: (n, p) => show(n, p),
    back() {
      if (backOverride && history[history.length - 1] === backOverride) history.pop();
      const target = backOverride ?? history.pop() ?? 'title';
      backOverride = null;
      show(target, target === 'settings' ? { overlay: true } : undefined, true);
    },
    startDaily() {
      const key = dateKey(now());
      const n = Math.max(1, dailyNumber(key));
      const cfg = { ...deps.generateMatchConfig(dailySeed(key)), competition: `Daily Match #${n}`, halfLengthSeconds: DAILY_RULES.halfLengthSeconds };
      void launch(cfg, options('daily', DAILY_RULES.difficulty, DAILY_RULES.halfLengthSeconds, 2_100_000, { daily: { dateKey: key, number: n } }));
    },
    startCareer() {
      const plan = nextCareerMatch(store.get().career);
      const cfg = { ...deps.generateMatchConfig(plan.seed), competition: plan.competition, drama: plan.drama, halfLengthSeconds: plan.halfLengthSeconds };
      void launch(cfg, options('career', plan.difficulty, plan.halfLengthSeconds, plan.baseAudience, {
        career: { tierId: plan.tier.id, tierIndex: plan.tierIndex, matchIndex: plan.matchIndex, matchNumber: plan.matchNumber, matchesInTier: plan.tier.matches },
      }));
    },
    startQuick(seed, difficulty, half) {
      const cfg = { ...deps.generateMatchConfig(seed), halfLengthSeconds: half };
      void launch(cfg, options('quick', difficulty, half, QUICK_AUDIENCE[difficulty]));
    },
    startTraining() {
      const cfg = { ...deps.generateMatchConfig(TRAINING_SEED), competition: TRAINING_COMPETITION, halfLengthSeconds: TRAINING_HALF_S };
      void launch(cfg, {
        mode: 'tutorial', difficulty: 'rookie', halfLengthSeconds: TRAINING_HALF_S, baseAudience: 45_000,
        unlockedCameras: [], graphicsPackage: 'gfx_basic', punchMode: 'hot',
      });
    },
    retry() {
      if (!last) return show('title');
      void launch(last.config, last.options);
    },
    lastStart: () => last,
    notify,
    settingsChanged(s) { applySettings(s); deps.onSettingsChanged?.(s); },
  };

  function build(name: ScreenName, params: unknown): ScreenInstance | null {
    switch (name) {
      case 'title': return titleScreen(ctx, (params ?? {}) as TitleParams);
      case 'quick': return quickScreen(ctx);
      case 'daily': return dailyScreen(ctx);
      case 'career': return careerScreen(ctx);
      case 'howto': return howtoScreen(ctx);
      case 'settings': return settingsScreen(ctx, (params ?? {}) as { overlay?: boolean });
      case 'pause': return pauseScreen(ctx);
      case 'halftime': return halftimeScreen(ctx, params as HalfTimeParams);
      case 'report': return reportScreen(ctx, params as { summary: ResultSummary; extras?: MatchExtras });
      case 'training_report': return trainingReportScreen(ctx, params as TrainingReportParams);
      case 'training_skip': return trainingSkipScreen(ctx);
      case 'none': return null;
    }
  }

  function show(name: ScreenName, params?: unknown, isBack = false) {
    const prev = cur;
    if (!isBack && prev && prev.name !== name && name !== 'none') {
      // Transient overlays don't go on the back stack except pause (for settings/howto from pause).
      if (!['report', 'halftime', 'training_report', 'training_skip'].includes(prev.name)) history.push(prev.name);
      if (history.length > 12) history.shift();
    }
    if (name === 'title' || name === 'none') history.length = 0;
    const p = params as { back?: ScreenName } | undefined;
    if (p?.back) backOverride = p.back;

    const inst = build(name, params);
    if (prev) {
      const old = prev.inst;
      old.el.classList.add('is-leaving');
      old.el.inert = true;
      setTimeout(() => { old.el.remove(); old.destroy?.(); }, prefersReducedMotion() ? 0 : 320);
    }
    cur = inst ? { name, inst } : null;
    el.classList.toggle('is-open', !!inst);
    el.classList.toggle('is-overlay', !!inst?.overlay);
    if (inst) {
      inst.el.classList.add('is-entering');
      el.append(inst.el);
      requestAnimationFrame(() => inst.el.classList.remove('is-entering'));
      inst.mounted?.();
    }
  }

  function stinger(): Promise<void> {
    if (prefersReducedMotion()) return Promise.resolve();
    return new Promise((resolve) => {
      stingerEl.classList.remove('is-playing');
      void stingerEl.offsetWidth;
      stingerEl.classList.add('is-playing');
      setTimeout(resolve, 520);
      setTimeout(() => stingerEl.classList.remove('is-playing'), 1150);
    });
  }

  let notifyTimer = 0;
  function notify(msg: string) {
    notifyEl.textContent = msg;
    notifyEl.classList.remove('is-on');
    void notifyEl.offsetWidth;
    notifyEl.classList.add('is-on');
    clearTimeout(notifyTimer);
    notifyTimer = window.setTimeout(() => notifyEl.classList.remove('is-on'), 2000);
  }

  const onKey = (e: KeyboardEvent) => {
    if (!cur) return;
    if (cur.inst.onKey?.(e)) { e.preventDefault(); e.stopImmediatePropagation(); return; }
    if (e.key === 'Escape' && !['title', 'report', 'halftime', 'training_report', 'training_skip'].includes(cur.name)) {
      e.preventDefault(); e.stopImmediatePropagation(); ctx.back();
    }
  };
  window.addEventListener('keydown', onKey, true);

  return {
    el, store,
    show: (n, p) => show(n, p),
    hide: () => show('none'),
    current: () => cur?.name ?? 'none',
    isOpen: () => !!cur,
    showReport: (summary, extras) => show('report', { summary, extras }),
    showHalfTime: (p) => show('halftime', p),
    showPause: () => show('pause'),
    startDaily: () => ctx.startDaily(),
    startCareer: () => ctx.startCareer(),
    startQuick: (s, d, hl) => ctx.startQuick(s, d, hl),
    startTraining: () => ctx.startTraining(),
    lastStart: () => last,
    stinger,
    destroy() { window.removeEventListener('keydown', onKey, true); el.remove(); stingerEl.remove(); notifyEl.remove(); },
  };
}
